import { resolveBloomUniversalSpaceHref } from '../constants/routes';
import type {
  BloomSpaceGridSpan,
  BloomUniversalAssetFields,
  BloomUniversalHubContent,
  BloomUniversalHubFields,
  BloomUniversalSpaceCardContent,
  BloomUniversalSpaceCardFields,
  BloomUniversalSpaceContent,
  BloomUniversalSpaceFields,
} from '../types/bloomUniversalContent';

const VALID_SPANS: readonly BloomSpaceGridSpan[] = [
  'full',
  'wide',
  'half',
  'third',
  'quarter',
  'tall',
];

function normalizeAssetUrl(
  asset: BloomUniversalAssetFields | undefined,
): string | undefined {
  const url = asset?.url?.trim();
  if (!url) {
    return undefined;
  }
  if (url.startsWith('//')) {
    return `https:${url}`;
  }
  // Absolute CDN URLs pass through; any leftover relative path is resolved at render.
  return url;
}

function normalizeGridSpan(value: string | undefined): BloomSpaceGridSpan {
  if (value && (VALID_SPANS as readonly string[]).includes(value)) {
    return value as BloomSpaceGridSpan;
  }
  return 'third';
}

/**
 * Resolves hub CTA target from Contentful fields.
 * Prefer `ctaHref`; legacy `ctaSpaceKey` → space page path.
 */
export function resolveHubCtaHref(
  ctaHref: string | undefined,
  ctaSpaceKey: string | undefined,
): string | undefined {
  const href = ctaHref?.trim();
  if (href) {
    return href;
  }
  const spaceKey = ctaSpaceKey?.trim();
  if (spaceKey) {
    return resolveBloomUniversalSpaceHref(spaceKey);
  }
  return undefined;
}

/** CMS `sortOrder`, then array index — used on all breakpoints (2-up or 4-up). */
export function orderPageCardFields(
  cards: readonly BloomUniversalSpaceCardFields[],
): BloomUniversalSpaceCardFields[] {
  return [...cards]
    .map((card, index) => ({ card, index }))
    .sort((a, b) => {
      const orderA =
        typeof a.card.sortOrder === 'number' &&
        Number.isFinite(a.card.sortOrder)
          ? a.card.sortOrder
          : a.index;
      const orderB =
        typeof b.card.sortOrder === 'number' &&
        Number.isFinite(b.card.sortOrder)
          ? b.card.sortOrder
          : b.index;
      return orderA - orderB || a.index - b.index;
    })
    .map(({ card }) => card);
}

function mapSpaceCardFields(
  fields: BloomUniversalSpaceCardFields,
): BloomUniversalSpaceCardContent | null {
  const cardKey = fields.cardKey?.trim();
  const title = fields.title?.trim();
  if (!cardKey || !title) {
    return null;
  }
  const subtitle = fields.subtitle?.trim() || undefined;
  const imageUrl = normalizeAssetUrl(fields.image);
  const cardHref = fields.cardHref?.trim() || undefined;
  if (fields.isActive === false) {
    return null;
  }
  return {
    cardKey,
    title,
    ...(subtitle ? { subtitle } : {}),
    ...(imageUrl ? { imageUrl } : {}),
    ...(cardHref ? { cardHref } : {}),
  };
}

export function mapBloomUniversalSpaceFields(
  fields: BloomUniversalSpaceFields,
): BloomUniversalSpaceContent {
  const isActive = fields.isActive !== false;
  const pageHeadline = fields.pageHeadline?.trim() || undefined;
  const pageBody = fields.pageBody?.trim() || undefined;
  const pageCtaLabel = fields.pageCtaLabel?.trim() || undefined;
  const pageCtaHref = fields.pageCtaHref?.trim() || undefined;
  const cardsSectionTitle = fields.cardsSectionTitle?.trim() || undefined;
  const tileImageUrl = normalizeAssetUrl(fields.tileImage);
  const iconUrl = normalizeAssetUrl(fields.icon);
  const pageHeroImageUrl = normalizeAssetUrl(fields.pageHeroImage);
  // Drop inactive detail cards (Contentful `isActive: false`).
  const pageCards = orderPageCardFields(fields.pageCards ?? [])
    .map(mapSpaceCardFields)
    .filter((card): card is BloomUniversalSpaceCardContent => card !== null);

  return {
    spaceKey: fields.spaceKey.trim(),
    title: fields.title.trim(),
    gridSpan: normalizeGridSpan(fields.gridSpan),
    sortOrder: Number.isFinite(fields.sortOrder) ? fields.sortOrder : 0,
    isActive,
    pageCards,
    ...(tileImageUrl ? { tileImageUrl } : {}),
    ...(iconUrl ? { iconUrl } : {}),
    ...(pageHeadline ? { pageHeadline } : {}),
    ...(pageBody ? { pageBody } : {}),
    ...(pageCtaLabel ? { pageCtaLabel } : {}),
    ...(pageCtaHref ? { pageCtaHref } : {}),
    ...(pageHeroImageUrl ? { pageHeroImageUrl } : {}),
    ...(cardsSectionTitle ? { cardsSectionTitle } : {}),
  };
}

/**
 * Maps hub fields (Contentful-shaped or fallback) → UI content.
 * Drops inactive spaces (`isActive: false`); sorts by `sortOrder`.
 * Inactive space cards are already dropped in `mapSpaceCardFields`.
 */
export function mapBloomUniversalHubFields(
  fields: BloomUniversalHubFields,
): BloomUniversalHubContent {
  const spaces = fields.spaces
    .map(mapBloomUniversalSpaceFields)
    .filter((space) => space.isActive && space.spaceKey.length > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const heroBackgroundUrl = normalizeAssetUrl(fields.heroBackground);
  const phoneIconUrl = normalizeAssetUrl(fields.phoneIcon);
  const arrowIconUrl = normalizeAssetUrl(fields.arrowIcon);
  const breadcrumbChevronIconUrl = normalizeAssetUrl(
    fields.breadcrumbChevronIcon,
  );
  const phoneNumber = fields.phoneNumber?.trim() || undefined;
  const ctaHref = resolveHubCtaHref(fields.ctaHref, fields.ctaSpaceKey);

  return {
    experienceEyebrow: fields.experienceEyebrow,
    headline: fields.headline,
    subheadline: fields.subheadline,
    ctaLabel: fields.ctaLabel,
    spaces,
    ...(fields.pageKey ? { pageKey: fields.pageKey } : {}),
    ...(ctaHref ? { ctaHref } : {}),
    ...(heroBackgroundUrl ? { heroBackgroundUrl } : {}),
    ...(phoneNumber ? { phoneNumber } : {}),
    ...(phoneIconUrl ? { phoneIconUrl } : {}),
    ...(arrowIconUrl ? { arrowIconUrl } : {}),
    ...(breadcrumbChevronIconUrl ? { breadcrumbChevronIconUrl } : {}),
    ...(fields.exploreTitle?.trim()
      ? { exploreTitle: fields.exploreTitle.trim() }
      : {}),
    ...(fields.exploreSubtitle?.trim()
      ? { exploreSubtitle: fields.exploreSubtitle.trim() }
      : {}),
  };
}

/** Mosaic section copy — Contentful `exploreTitle` / `exploreSubtitle`, then i18n fallbacks. */
export function resolveHubExploreCopy(
  hub: Pick<BloomUniversalHubContent, 'exploreTitle' | 'exploreSubtitle'>,
  fallbacks: Readonly<{ exploreTitle: string; exploreSubtitle: string }>,
): { readonly exploreTitle: string; readonly exploreSubtitle: string } {
  return {
    exploreTitle: hub.exploreTitle?.trim() || fallbacks.exploreTitle,
    exploreSubtitle: hub.exploreSubtitle?.trim() || fallbacks.exploreSubtitle,
  };
}

export function findSpaceInHub(
  hub: BloomUniversalHubContent,
  spaceKey: string | undefined,
): BloomUniversalSpaceContent | undefined {
  if (!spaceKey) {
    return undefined;
  }
  return hub.spaces.find((space) => space.spaceKey === spaceKey);
}
